/**
 * ⭐⭐⭐ **Antrean bump dapur** — supaya "sudah keluar" tak hilang saat jaringan berkedip.
 *
 * ### ⛔ Masalah yang ditutupnya
 * `bump` menyingkirkan tiket dari layar **sebelum** server menjawab (juru masak sudah beralih ke
 * tiket berikutnya sebelum jaringan sempat membalas). Kalau permintaannya lalu gagal, pembacaan
 * berikutnya mengembalikan tiket itu ke layar — dan hidangan yang sudah diantar **dimasak dua
 * kali**. Di jam sibuk itu tak terlihat sebagai galat, cuma sebagai dapur yang tiba-tiba kacau.
 *
 * ### ⛔⛔ Kenapa antrean SENDIRI, bukan menumpang `outbox.ts`
 * `outbox.ts` menyatakan dirinya sengaja satu antrean — dan alasannya eksplisit: *"yang dijaga
 * keduanya sama persis, yaitu **uang** yang sudah masuk laci tapi belum sampai ke portal."* Bump
 * bukan uang. Menumpangkannya berarti `pendingCount()` — angka yang dibaca KASIR sebelum menutup
 * sesi untuk memutuskan apakah aman menutup — ikut menghitung tiket dapur. Angka yang dipakai
 * memutuskan soal uang tak boleh bergerak karena hal yang bukan uang.
 *
 * ⚠️ Lagipula keduanya hidup di PERANGKAT yang berbeda: antrean penjualan di till, antrean ini di
 * tablet dapur. Satu modul yang dipakai dua perangkat untuk dua hal berbeda cuma terlihat hemat.
 *
 * ### ⭐ Aman dikirim ulang, dan itu bukan asumsi
 * Server menolak bump ganda dengan `KDS-409-ALREADY_DONE` (`kitchen.ts`). Penolakan itu di sini
 * dibaca sebagai **BERHASIL**: yang diinginkan antrean adalah "tiket ini selesai", dan ia memang
 * sudah selesai. Memperlakukannya sebagai galat membuat antrean mencoba selamanya untuk pekerjaan
 * yang sudah beres.
 *
 * ### ⚠️ Yang ditolak DIBUANG di sini — kebalikan dari `outbox.ts`, dan itu disengaja
 * Di sana yang ditolak ditahan (`needs_review`) karena penjualan yang dibuang **lenyap tanpa
 * jejak**: uangnya sudah diterima dan tak ada salinan di mana pun. Di sini kebenarannya ada di
 * SERVER dan digambar ulang tiap beberapa detik — kalau bumpnya benar-benar tak berlaku, tiketnya
 * muncul lagi di layar, dan itulah umpan baliknya. Menahan baris yang tak bisa apa-apa cuma
 * melahirkan penghitung yang tak pernah turun, dan penghitung begitu berhenti dibaca.
 */
import { api, ApiFailure } from './client';

const KEY = 'osbpos.kds.outbox';

export interface BumpItem {
  /** `ticket` = seluruh tiket keluar · `line` = satu hidangan di dalamnya. */
  readonly jenis: 'ticket' | 'line';
  readonly id: string;
  /** Untuk ditampilkan bila kelak perlu; tak mengubah semantik apa pun. */
  readonly orderNo?: string;
  readonly queuedAt: string;
}

function read(): BumpItem[] {
  try {
    const raw = localStorage.getItem(KEY);
    return raw === null ? [] : (JSON.parse(raw) as BumpItem[]);
  } catch {
    /* ⚠️ Antrean rusak ⇒ jangan melempar. Layar dapur yang mati karena satu byte rusak jauh lebih
       buruk daripada beberapa bump yang hilang — yang hilang toh muncul lagi sebagai tiket. */
    return [];
  }
}

function write(items: readonly BumpItem[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(items));
  } catch {
    /* kuota penuh / mode privat — lihat `cache.ts` */
  }
}

/**
 * ⛔ Kegagalan **transport** (belum pernah sampai) vs **keputusan server** (sudah sampai, dijawab).
 *
 * ⚠️ Membacanya terbalik adalah bug yang sudah menipu sekali di `outbox.ts`: setiap penolakan
 * server terbaca sebagai jaringan mati, dan antreannya mencoba selamanya. 5xx dihitung transport —
 * server sedang sakit itu keadaan sementara, bukan keputusan.
 */
export function gagalTransport(e: unknown): boolean {
  if (!(e instanceof ApiFailure)) return true;
  return e.detail.error_code.startsWith('HTTP-5');
}

/**
 * Antrekan satu bump yang belum sampai. **Sinkron** — begitu kembali, ia sudah aman dari muat-ulang.
 *
 * ⚠️ Idempoten di sisi klien juga: dua ketukan pada tiket yang sama tak boleh mengantre dua kali.
 */
export function enqueueBump(item: Omit<BumpItem, 'queuedAt'>): void {
  const ada = read();
  if (ada.some((x) => x.jenis === item.jenis && x.id === item.id)) return;
  write([...ada, { ...item, queuedAt: new Date().toISOString() }]);
}

/** Berapa bump yang belum diakui server. ⭐ Ditampilkan di pita atas — lihat catatan modul. */
export function pendingBumps(): number {
  return read().length;
}

/**
 * Kirim antrean, berurutan.
 *
 * ⛔ Berhenti pada kegagalan transport pertama: jaringan sedang mati, dan mencoba sisanya cuma
 * membuang waktu tablet yang justru paling terbatas.
 *
 * @returns berapa yang berhasil diakui server (termasuk yang dijawab "sudah selesai").
 */
export async function flushBumps(): Promise<number> {
  if (flushing) return 0;
  flushing = true;
  let acked = 0;
  try {
    for (const item of read()) {
      try {
        await (item.jenis === 'ticket'
          ? api.kitchenBump(item.id)
          : api.kitchenBumpLine(item.id));
        acked += 1;
        drop(item);
      } catch (e) {
        if (gagalTransport(e)) break;
        /*
         * ⭐ Jawaban tegas dari server — termasuk `KDS-409-ALREADY_DONE`, yang justru berarti
         * pekerjaannya sudah beres. Barisnya dibuang; kebenarannya ada di layar, yang membaca
         * ulang dari server beberapa detik lagi.
         */
        drop(item);
      }
    }
    return acked;
  } finally {
    flushing = false;
  }
}

let flushing = false;

function drop(item: BumpItem): void {
  write(read().filter((x) => !(x.jenis === item.jenis && x.id === item.id)));
}

/** Hanya untuk uji. ⛔ Jangan dipanggil dari alur dapur. */
export function clearBumpQueue(): void {
  localStorage.removeItem(KEY);
}
